"use client"

import React, { useEffect, useState } from "react"
import NextLink from "next/link"
import * as htmlparser2 from "htmlparser2"
import TC_Wrapper from "react-tag-commander"

import { IKTRichText } from "@/types/kontent.types"
import { getSyncDictionary } from "@/lib/sync-dictionary"
import { cn, GetSlug } from "@/lib/utils"
import { Typography } from "@/components/ui/atoms"

type TSummaryList = {
  label: string
  link: string
}

type TSummaryProps = {
  className?: string
  title: string
  description?: string
  body: IKTRichText
  isSticky: boolean
  isDown: boolean
  list?: TSummaryList[]
  activeSectionId?: string | null
}

// Building the summary based on the content of the article
function createSummary(body: IKTRichText): TSummaryList[] {
  let summary: Array<{ label: string; link: string }> = []
  let isH2 = false
  let label = ""
  let link = ""

  let parser = new htmlparser2.Parser({
    onopentag(name) {
      if (name === "h2") {
        isH2 = true
      }
    },
    ontext(text) {
      if (isH2 && text.trim() !== "") {
        label += text
      }
    },
    onclosetag(name) {
      if (name === "h2") {
        if (label) {
          const slugifiedText = GetSlug(label)
          link = `#${slugifiedText}`
          summary.push({ label: label, link: link })
        }
        isH2 = false
        label = ""
        link = ""
      }
    },
  })

  parser.write(body.html)
  parser.end()

  return summary
}

const Summary = ({
  className,
  title,
  description,
  body,
  list,
  activeSectionId,
  isSticky,
  isDown = false,
}: TSummaryProps) => {
  const wrapper = TC_Wrapper.getInstance()
  list = createSummary(body)
  const [activeEl, setActiveEl] = useState<number | null>(null)

  function generateNavigationEvent(
    event: React.MouseEvent<HTMLAnchorElement>,
    label: string,
    index: number
  ) {
    setTimeout(() => {
      setActiveEl(index)
      wrapper.triggerEvent("action", event.currentTarget, {
        click: label,
        click_chapter1: getSyncDictionary().page.news.summary,
      })
    }, 800)
  }

  useEffect(() => {
    const handleScroll = () => {
      const sections = document.querySelectorAll("h2[id]")
      let currentActiveEl = null
      for (let i = 0; i < sections.length; i++) {
        const section = sections[i]
        const nextSection = sections[i + 1]
        const sectionRect = section.getBoundingClientRect()
        if (nextSection) {
          const nextSectionRect = nextSection.getBoundingClientRect()
          if (
            nextSectionRect &&
            sectionRect.top < window.innerHeight &&
            nextSectionRect.top > 0
          ) {
            currentActiveEl = i
            break
          }
        } else if (sectionRect.top < window.innerHeight) {
          currentActiveEl = i
          break
        }
      }
      setActiveEl(currentActiveEl)
    }

    window.addEventListener("scroll", handleScroll)

    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  return (
    <aside
      className={cn(
        "top-15 flex flex-col gap-6 lg:w-[360px]",
        className,
        isSticky ? "sticky" : null,
        isDown ? "xl:top-[240px]" : "xl:top-[220px]"
      )}
    >
      {title || description ? (
        <header className="flex flex-col gap-6">
          {title ? (
            <Typography variant="headingS" as="span">
              {title}
            </Typography>
          ) : null}
          {description ? (
            <Typography as="p" variant="paragraphM">
              {description}
            </Typography>
          ) : null}
        </header>
      ) : null}
      <section className="bg-neutral-100 p-6">
        <ol type="1" className="list flex flex-col gap-6">
          {list
            ? list.map((item, index) => {
                return (
                  <li key={item.link} className="list list--link flex gap-4">
                    <span className="paragraph-s min-w-[14px]">
                      {index + 1}.
                    </span>
                    <NextLink
                      onClick={(e) =>
                        generateNavigationEvent(e, item.label, index)
                      }
                      className={cn(
                        "paragraph-s hover:text-dark hover:underline",
                        activeEl === index ||
                          activeSectionId === item.link.substring(1)
                          ? "font-600"
                          : ""
                      )}
                      href={item.link}
                    >
                      {item.label}
                    </NextLink>
                  </li>
                )
              })
            : null}
        </ol>
      </section>
    </aside>
  )
}

export { Summary, type TSummaryList, type TSummaryProps }
